import { Trans, useLingui } from '@lingui/react/macro'

import { ErrorAlert } from '$src/components/ErrorAlert'
import {
  maintenanceLogHooks,
  maintenanceTaskHooks,
  packageVersionTaskHooks,
  propertyHooks,
} from '$src/lib/trpcHooks'
import {
  ActionIcon,
  Box,
  Card,
  Group,
  Loader,
  rem,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core'
import { PlaceholderContent } from '@markku/components'
import {
  isWarrantyExpired,
  isWarrantyExpiringSoon,
  type WarrantyMetadata,
} from '@markku/types'
import {
  IconBolt,
  IconBuildingWarehouse,
  IconChartLine,
  IconChevronRight,
  IconDroplet,
  IconFileText,
  IconFlame,
  IconHistory,
  IconHome,
  IconShield,
  IconWind,
} from '@tabler/icons-react'
import { Link } from '@tanstack/react-router'
import { FinancialQuickCard } from './components/FinancialQuickCard'
import { MarkkuSavingsCard } from './components/MarkkuSavingsCard'

interface MaintenanceCategory {
  id: string
  title: string
  icon: React.ReactNode
  taskCount: number
  color: string
  bgColor: string
}

interface SummaryProps {
  propertyId: number
}

export function Summary({ propertyId }: SummaryProps) {
  const { t } = useLingui()
  // Fetch property for warranty data
  const { data: property, isLoading: propertyLoading } =
    propertyHooks.useGetById(propertyId)

  // Fetch all maintenance tasks
  const { data: tasksData, isLoading: tasksLoading } =
    maintenanceTaskHooks.useList({
      pageSize: 100,
    })

  // Fetch maintenance logs for this property
  const { data: logsData, isLoading: logsLoading } =
    maintenanceLogHooks.useList({
      pageSize: 100,
      filters: {
        $and: [
          { propertyId: { eq: propertyId } },
          { completedDate: { isNull: false } },
        ],
      },
    })

  // Fetch service package tasks to know what Markku handles
  const { data: packageTasksData, isLoading: packageTasksLoading } =
    packageVersionTaskHooks.useList({
      pageSize: 100,
    })

  // Loading state
  if (tasksLoading || logsLoading || packageTasksLoading || propertyLoading) {
    return (
      <Stack align="center" justify="center" h={300}>
        <Loader size="lg" />
        <Text size="sm" c="dimmed">
          <Trans>Loading summary...</Trans>
        </Text>
      </Stack>
    )
  }

  // Error state
  if (!tasksData || !logsData || !packageTasksData) {
    return <ErrorAlert />
  }

  const tasks = tasksData.data || []

  // Calculate warranty statistics
  const warranties =
    property?.attachments?.filter(
      (att) =>
        att.metadata &&
        typeof att.metadata === 'object' &&
        'category' in att.metadata &&
        att.metadata.category === 'warranty',
    ) || []

  const warrantyStats = {
    total: warranties.length,
    active: warranties.filter(
      (w) => !isWarrantyExpired((w.metadata as WarrantyMetadata).expiryDate),
    ).length,
    expiringSoon: warranties.filter((w) =>
      isWarrantyExpiringSoon((w.metadata as WarrantyMetadata).expiryDate),
    ).length,
  }

  // Group tasks by category for the category cards
  const categoryGroups: Record<string, number> = {}
  tasks.forEach((task) => {
    const category = task.category || 'other'
    categoryGroups[category] = (categoryGroups[category] || 0) + 1
  })

  const categories: MaintenanceCategory[] = [
    {
      id: 'roof',
      title: t`Roof and attic`,
      icon: <IconHome size={24} />,
      taskCount: categoryGroups['roof'] || 0,
      color: 'red.6',
      bgColor: 'red.0',
    },
    {
      id: 'facade',
      title: t`Facade`,
      icon: <IconBuildingWarehouse size={24} />,
      taskCount: categoryGroups['facade'] || 0,
      color: 'blue.6',
      bgColor: 'blue.0',
    },
    {
      id: 'electrical',
      title: t`Electrical systems`,
      icon: <IconBolt size={24} />,
      taskCount: categoryGroups['electrical'] || 0,
      color: 'yellow.6',
      bgColor: 'yellow.0',
    },
    {
      id: 'hvac',
      title: t`HVAC systems`,
      icon: <IconDroplet size={24} />,
      taskCount: categoryGroups['hvac'] || 0,
      color: 'cyan.6',
      bgColor: 'cyan.0',
    },
    {
      id: 'ventilation',
      title: t`Ventilation`,
      icon: <IconWind size={24} />,
      taskCount: categoryGroups['ventilation'] || 0,
      color: 'green.6',
      bgColor: 'green.0',
    },
    {
      id: 'heating',
      title: t`Heating systems`,
      icon: <IconFlame size={24} />,
      taskCount: categoryGroups['heating'] || 0,
      color: 'orange.6',
      bgColor: 'orange.0',
    },
  ]

  return (
    <Stack gap="lg">
      {/* Property Settings */}
      <Card
        radius="lg"
        withBorder
        component={Link}
        to={`/properties/${propertyId}`}
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <Group justify="space-between">
          <Stack gap="xs">
            <Group gap="xs">
              <IconHome size={20} color="var(--mantine-color-gray-6)" />
              <Text fw={600}>
                <Trans>Property details</Trans>
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              <Trans>Manage property information</Trans>
            </Text>
          </Stack>
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Card>

      {/* Financial Quick Summary */}
      <FinancialQuickCard propertyId={propertyId} />

      {/* Consumption Tracking */}
      <Card
        radius="lg"
        withBorder
        component={Link}
        to="/consumption"
        // @ts-expect-error -- TODO: fix-baseline
        search={{ propertyId: propertyId.toString() }}
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <Group justify="space-between">
          <Stack gap="xs">
            <Group gap="xs">
              <IconChartLine size={20} color="var(--mantine-color-gray-6)" />
              <Text fw={600}>
                <Trans>Consumption tracking</Trans>
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              <Trans>Electricity, water and heating</Trans>
            </Text>
          </Stack>
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Card>

      {/* Maintenance History */}
      <Card
        radius="lg"
        withBorder
        component={Link}
        to="/maintenance-log"
        // @ts-expect-error -- TODO: fix-baseline
        search={{ propertyId: propertyId.toString() }}
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <Group justify="space-between">
          <Stack gap="xs">
            <Group gap="xs">
              <IconHistory size={20} color="var(--mantine-color-gray-6)" />
              <Text fw={600}>
                <Trans>Full maintenance history</Trans>
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              <Trans>List of all maintenance records</Trans>
            </Text>
          </Stack>
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Card>

      {/* Documents */}
      <Card
        radius="lg"
        withBorder
        component={Link}
        to={`/properties/${propertyId}`}
        hash="documents"
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <Group justify="space-between">
          <Stack gap="xs">
            <Group gap="xs">
              <IconFileText size={20} color="var(--mantine-color-gray-6)" />
              <Text fw={600}>
                <Trans>Documents</Trans>
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              <Trans>Certificates, manuals and receipts</Trans>
            </Text>
          </Stack>
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Card>

      {/* Warranties */}
      <Card
        radius="lg"
        withBorder
        component={Link}
        to={`/properties/${propertyId}`}
        hash="warranties"
        style={{ cursor: 'pointer', textDecoration: 'none' }}
      >
        <Group justify="space-between">
          <Stack gap="xs">
            <Group gap="xs">
              <IconShield size={20} color="var(--mantine-color-green-6)" />
              <Text fw={600}>
                <Trans>Warranties</Trans>
              </Text>
            </Group>
            <Group gap="sm">
              <Text size="sm" c="dimmed">
                <Trans>{warrantyStats.active} active</Trans>
              </Text>
              {warrantyStats.expiringSoon > 0 && (
                <Text size="sm" c="yellow.6" fw={500}>
                  <Trans>{warrantyStats.expiringSoon} expiring soon</Trans>
                </Text>
              )}
            </Group>
          </Stack>
          <ActionIcon variant="subtle" color="gray">
            <IconChevronRight size={20} />
          </ActionIcon>
        </Group>
      </Card>

      <PlaceholderContent>
        {/* Maintenance Categories */}
        {categories.length > 0 && (
          <Stack gap="sm">
            <Text fw={600} size="lg">
              <Trans>Maintenance categories</Trans>
            </Text>

            <SimpleGrid cols={1} spacing="sm">
              {categories.map((category: MaintenanceCategory) => (
                <Card
                  key={category.id}
                  radius="lg"
                  withBorder
                  style={{ cursor: 'pointer' }}
                >
                  <Group justify="space-between">
                    <Group gap="md">
                      <Box
                        style={{
                          backgroundColor: `var(--mantine-color-${category.bgColor})`,
                          borderRadius: rem(12),
                          width: rem(48),
                          height: rem(48),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box c={category.color}>{category.icon}</Box>
                      </Box>
                      <Stack gap={2}>
                        <Text fw={600}>{category.title}</Text>
                        <Text size="sm" c="dimmed">
                          <Trans>{category.taskCount} tasks</Trans>
                        </Text>
                      </Stack>
                    </Group>
                    <ActionIcon variant="subtle" color="gray">
                      <IconChevronRight size={20} />
                    </ActionIcon>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          </Stack>
        )}
      </PlaceholderContent>

      {/* Markku Savings Card */}
      <PlaceholderContent>
        <MarkkuSavingsCard propertyId={propertyId} />
      </PlaceholderContent>
    </Stack>
  )
}
